"use client";

import { getCurrencyDropdownQueryClient } from "@workspace/casino_core/queries/CurrencyDropdownQuery.client";
import type { CashierSite } from "@workspace/casino_core/schema";
import { useSelectedLayoutSegments } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";
import isGamePageRoute from "../isGamePageRoute";
import isOriginalGameRoute from "../isOriginalGameRoute";
import type { CurrencyItem } from "./getCurrencyDropdownData";

type Params = {
  brand: string;
  currentCurrency: string;
  currentBalance: number;
  currencyItems: CurrencyItem[];
  originalGameHandles?: string[];
};

type Fetched = {
  seed: CurrencyItem[];
  balances: Record<string, number>;
  syncedAt: number;
};

const useHeaderBalances = ({
  brand,
  currentCurrency,
  currentBalance,
  currencyItems,
  originalGameHandles,
}: Params) => {
  const segments = useSelectedLayoutSegments();
  const route = segments.join("/");
  const isOriginalGame =
    isGamePageRoute(segments) &&
    isOriginalGameRoute(segments, originalGameHandles);
  const [fetched, setFetched] = useState<Fetched>();
  const requestRef = useRef(0);
  const routeRef = useRef(route);

  const refresh = useCallback(async () => {
    const request = ++requestRef.current;
    const seed = currencyItems;
    const { data } = await getCurrencyDropdownQueryClient({
      input: { site: brand as CashierSite },
    }).catch(() => ({ data: undefined }));
    const edges = data?.banking?.myAllBalances?.edges;
    if (!edges || request !== requestRef.current) return;
    const balances = Object.fromEntries(
      edges.flatMap((edge) =>
        edge?.node?.currency
          ? [[edge.node.currency, edge.node.cashierBalance ?? 0] as const]
          : [],
      ),
    );
    setFetched({ seed, balances, syncedAt: request });
  }, [brand, currencyItems]);

  useEffect(() => {
    if (routeRef.current === route) return;
    routeRef.current = route;
    if (isOriginalGame) requestRef.current++;
    else refresh();
  }, [route, isOriginalGame, refresh]);

  const current = fetched?.seed === currencyItems ? fetched : undefined;
  const balances: Record<string, number> = {
    ...Object.fromEntries(
      currencyItems.map((item) => [item.currency, item.cashierBalance]),
    ),
    [currentCurrency]: currentBalance,
    ...current?.balances,
  };

  return { balances, syncedAt: current?.syncedAt ?? 0, refresh };
};

export default useHeaderBalances;
